// Geometry helpers for L-shape playouts.
//
// A SuperSource box scales its source uniformly, so its normalized width and
// height both equal `size`. That is what makes the L self-consistent: when the
// program window is snapped flush into a corner, the sidebar's width fraction
// and the banner's height fraction are both `1 - size`, which is exactly the
// constraint a non-16:9 notch would otherwise force you to solve with
// letterboxing or crop.
//
// Geometry is stored as center + size rather than a corner enum so the operator
// can nudge off the snap to match artwork that isn't mathematically flush.
/** Default magnet radius, in normalized units. */
export const SNAP_TOLERANCE = 0.01;
/**
 * Center position that places a window of `size` flush into a corner.
 *
 * Flush means the box's outer edges sit exactly on the frame edges: for
 * top-left, `x - size/2 === 0` and `y - size/2 === 0`.
 */
export function snapPosition(target, size) {
    const half = size / 2;
    switch (target) {
        case "top-left":
            return { x: half, y: half };
        case "top-right":
            return { x: 1 - half, y: half };
        case "bottom-left":
            return { x: half, y: 1 - half };
        case "bottom-right":
            return { x: 1 - half, y: 1 - half };
    }
}
/**
 * Which corner a geometry is snapped to, or null when it is not flush against
 * two edges. Used by the editor to warn that the result isn't an L.
 */
export function detectSnapTarget(geometry, tolerance = SNAP_TOLERANCE) {
    const targets = [
        "top-left",
        "top-right",
        "bottom-left",
        "bottom-right",
    ];
    for (const target of targets) {
        const { x, y } = snapPosition(target, geometry.size);
        if (Math.abs(x - geometry.x) <= tolerance &&
            Math.abs(y - geometry.y) <= tolerance) {
            return target;
        }
    }
    return null;
}
/** True when the window forms a valid L (flush against two frame edges). */
export function isFlush(geometry, tolerance = SNAP_TOLERANCE) {
    return detectSnapTarget(geometry, tolerance) !== null;
}
/**
 * Thickness of the L's arms in pixels, so the artwork can be drawn to match.
 * Only meaningful for a flush window.
 */
export function barThickness(size, frameWidth, frameHeight) {
    return {
        sidebar: Math.round((1 - size) * frameWidth),
        banner: Math.round((1 - size) * frameHeight),
    };
}
/** The rectangle the program window occupies, normalized. */
export function windowRect(geometry) {
    return {
        left: geometry.x - geometry.size / 2,
        top: geometry.y - geometry.size / 2,
        width: geometry.size,
        height: geometry.size,
    };
}
/** Fullscreen state - where every squeeze starts and ends. */
export const FULLSCREEN_GEOMETRY = { x: 0.5, y: 0.5, size: 1 };
export const EASING = {
    linear: (t) => t,
    "ease-in": (t) => t * t,
    "ease-out": (t) => t * (2 - t),
    "ease-in-out": (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
};
/**
 * Interpolate the squeeze.
 *
 * Because the box's left edge is `x - size/2` - linear in both x and size -
 * lerping x, y and size together keeps a flush window pinned to its corner on
 * every intermediate frame. The picture shrinks *into* the corner rather than
 * drifting toward it, with no special-casing.
 */
export function interpolateGeometry(from, to, progress, easing = "ease-in-out") {
    const t = EASING[easing](Math.min(1, Math.max(0, progress)));
    return {
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
        size: from.size + (to.size - from.size) * t,
    };
}
/**
 * Total seconds an item occupies the output: squeeze in, hold, squeeze out.
 * Used by the scheduler to decide whether a late item can still finish before
 * the next one is due.
 */
export function itemDurationSeconds(inFrames, holdSeconds, outFrames, fps) {
    return inFrames / fps + holdSeconds + outFrames / fps;
}
